import React, { useState, useEffect, useCallback } from 'react';
import { 
  ExclamationTriangleIcon,
  ArchiveBoxIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import inventoryAlertsService from '../../services/InventoryAlertsService';

const InventoryAlertsWidget = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [alertsSummary, setAlertsSummary] = useState({
    outOfStockCount: 0,
    lowStockCount: 0,
    totalAlertsCount: 0,
    inStockCount: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.company_id) {
      loadAlertsSummary();
      // Check for new alerts and create notifications
      checkForNewAlerts();
    }
  }, [user?.company_id]);

  const loadAlertsSummary = useCallback(async () => {
    try {
      setLoading(true);
      const summary = await inventoryAlertsService.getAlertsSummary(user.company_id);
      setAlertsSummary(summary);
    } catch (error) {
      console.error('Error loading inventory alerts summary:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.company_id]);

  const checkForNewAlerts = useCallback(async () => {
    try {
      await inventoryAlertsService.checkAndCreateAlerts(user.company_id, user.id);
    } catch (error) {
      console.error('Error checking for new inventory alerts:', error);
    }
  }, [user?.company_id, user?.id]);

  const handleViewInventory = () => {
    // Only jump to the alerts filter when there ARE alerts; from the "All Systems Good" state this
    // must land on the full items list, not an empty alerts view (which reads as "no inventory").
    const hasAnyAlerts = (alertsSummary?.totalAlertsCount || 0) > 0;
    navigate(hasAnyAlerts ? '/operations/inventory?tab=items&filter=alerts' : '/operations/inventory?tab=items');
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-white/80 dark:from-slate-900/80 to-blue-50/40 dark:to-blue-900/20 backdrop-blur-xl rounded-lg shadow-2xl border border-blue-200/50 dark:border-blue-900/50-sm border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  const hasAlerts = alertsSummary.totalAlertsCount > 0;
  const isEmpty = !hasAlerts && alertsSummary.inStockCount === 0;

  return (
    <div className="bg-gradient-to-br from-white/85 dark:from-slate-800/85 to-gray-50/60 dark:to-slate-900/40 rounded-xl shadow-lg border border-blue-200/50 dark:border-blue-900/50 overflow-hidden backdrop-blur-xl transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
      {/* Header */}
      <div className="px-6 py-4 border-b bg-gradient-to-r from-slate-600 dark:from-slate-600/80 to-gray-600 dark:to-gray-600/70 border-slate-400/30 backdrop-blur-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white/20 dark:bg-white/10 rounded-xl shadow-sm flex items-center justify-center backdrop-blur-sm">
              <ArchiveBoxIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Inventory Status</h3>
              <p className="text-sm text-white/80">
                {hasAlerts ? 'Attention Required' : isEmpty ? 'Not set up yet' : 'All Systems Good'}
              </p>
            </div>
          </div>
          {hasAlerts ? (
            <ExclamationTriangleIcon className="h-8 w-8 text-yellow-500" />
          ) : isEmpty ? (
            <ArchiveBoxIcon className="h-8 w-8 text-white/70" />
          ) : (
            <CheckCircleIcon className="h-8 w-8 text-green-500" />
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {hasAlerts ? (
          <div className="space-y-6">
            {/* Alert Cards */}
            <div className="grid grid-cols-1 gap-4">
              {alertsSummary.outOfStockCount > 0 && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <XCircleIcon className="h-6 w-6 text-red-600" />
                      </div>
                      <div className="ml-3">
                        <h4 className="text-sm font-semibold text-red-800 dark:text-red-200">
                          Out of Stock Items
                        </h4>
                        <p className="text-xs text-red-700 dark:text-red-300">
                          Immediate attention required
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-red-700 dark:text-red-300">
                        {alertsSummary.outOfStockCount}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {alertsSummary.lowStockCount > 0 && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />
                      </div>
                      <div className="ml-3">
                        <h4 className="text-sm font-semibold text-yellow-800 dark:text-yellow-200">
                          Low Stock Items
                        </h4>
                        <p className="text-xs text-yellow-700 dark:text-yellow-300">
                          Consider reordering soon
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-yellow-700 dark:text-yellow-300">
                        {alertsSummary.lowStockCount}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Summary Stats */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-900 mb-1">
                  {alertsSummary.totalAlertsCount}
                </div>
                <div className="text-sm text-gray-600 mb-3">
                  {alertsSummary.totalAlertsCount === 1 ? 'Item needs attention' : 'Items need attention'}
                </div>
                <div className="text-xs text-gray-500">
                  {alertsSummary.inStockCount} {alertsSummary.inStockCount === 1 ? 'item' : 'items'} in good condition
                </div>
              </div>
            </div>

            {/* Action Button */}
            <button
              onClick={handleViewInventory}
              className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 shadow-sm transition-all duration-200"
            >
              <ArchiveBoxIcon className="h-4 w-4 mr-2" />
              View Inventory Details
              <ArrowRightIcon className="ml-2 h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="text-center py-6">
            <div className="mb-4">
              {isEmpty ? (
                <>
                  <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                    <ArchiveBoxIcon className="h-8 w-8 text-gray-400" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">
                    No Inventory Tracked Yet
                  </h4>
                  <p className="text-sm text-gray-600">
                    Add items to start tracking stock levels
                  </p>
                </>
              ) : (
                <>
                  <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-3">
                    <CheckCircleIcon className="h-8 w-8 text-green-600" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">
                    All Stock Levels Good
                  </h4>
                  <p className="text-sm text-gray-600">
                    {alertsSummary.inStockCount} {alertsSummary.inStockCount === 1 ? 'item' : 'items'} properly stocked
                  </p>
                </>
              )}
            </div>

            <button
              onClick={handleViewInventory}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
            >
              <ArchiveBoxIcon className="h-4 w-4 mr-2" />
              View Inventory
              <ArrowRightIcon className="ml-2 h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryAlertsWidget;

